/**
 * useSidebarBlacklist — geteilter Client-State fuer die Union der Gruppen-
 * Sidebar-Blacklists des angemeldeten Users.
 *
 * Backend liefert unter GET /portal/me/sidebar-prefs die Union der
 * Blacklists aller Adressbuch-Gruppen des Users. Admin + GF bekommen
 * eine leere Liste (Wildcard-Bypass) — siehe [[feedback_gf_darf_alles]].
 *
 * Warum shared Module-Level State statt jeder Consumer eigener fetch:
 *   - AssistantAvatar sitzt in der Topbar (mobile + desktop), PortalSidebar
 *     im Desktop-Rail, MobileHomeTabBar in der mobile Tab-Bar. Alle drei
 *     brauchen die gleiche Liste. Ohne Cache waeren das 3 Requests bei
 *     jedem Login.
 *   - Filterung ist Read-Only aus Consumer-Sicht — der Editor sitzt in
 *     AdminAdressbuchGroupDetailPage und ruft PUT selbst; ein "refresh"-
 *     Signal ueber CustomEvent reicht, damit alle Consumer sofort
 *     re-fetchen wenn der Admin was aendert.
 */
import { useEffect, useState } from 'react';
import { api } from '@onvice/portal-shell-api';

const EVT_REFRESH = 'onvice:sidebar-blacklist:refresh';

let cache: Set<string> | null = null;
let inflight: Promise<Set<string>> | null = null;
const listeners = new Set<(s: Set<string>) => void>();

async function fetchOnce(): Promise<Set<string>> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await api.get<{ success: boolean; data: { blacklist: string[] } }>(
        '/portal/me/sidebar-prefs'
      );
      const next = new Set<string>(res.success ? res.data.blacklist ?? [] : []);
      cache = next;
      listeners.forEach((cb) => cb(next));
      return next;
    } catch {
      const empty = new Set<string>();
      cache = empty;
      return empty;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function refreshSidebarBlacklist(): void {
  cache = null;
  window.dispatchEvent(new CustomEvent(EVT_REFRESH));
  void fetchOnce();
}

/**
 * Aktueller Blacklist-Slug-Set. Fetch-lazy beim ersten Mount.
 * Consumer entscheidet was er mit dem leeren Set (noch nicht geladen)
 * macht — meistens: nichts filtern bis Werte da sind.
 */
export function useSidebarBlacklist(): Set<string> {
  const [slugs, setSlugs] = useState<Set<string>>(cache ?? new Set());

  useEffect(() => {
    listeners.add(setSlugs);
    if (cache === null) void fetchOnce();
    else setSlugs(cache);
    const onRefresh = () => { void fetchOnce().then(setSlugs); };
    window.addEventListener(EVT_REFRESH, onRefresh);
    return () => {
      listeners.delete(setSlugs);
      window.removeEventListener(EVT_REFRESH, onRefresh);
    };
  }, []);

  return slugs;
}
